// ============================================
// Shop Overlay
// Replaces ShopOverlayController.cs
// ============================================

import { events, Events } from '../engine/events.js';
import { UITags, EquipSlotNames } from '../engine/constants.js';
import { variableManager } from '../engine/variables.js';
import { L } from '../systems/localization.js';
import { isPat } from '../engine/pgate.js';
import { statsUI } from './stats.js';
import { appearanceState } from '../systems/appearance.js';
import { inventoryManager } from '../systems/inventory.js';

// Parity with the wardrobe: a masc-clothing item locked above the Fem cap must
// show the same locked state in the shop's Try On / Equip button, not a working
// "Equip" that silently no-ops in the shop:equip handler. Kept TAPPABLE (override
// pointer-events) so the reason surfaces as a toast on touch, since a title
// tooltip is hover-only and invisible on iOS. foundby:VPS iPhone player 0.2.9.0.6.
function _markShopLockedBtn(btn, label, reason) {
    btn.textContent = label;
    if (reason) btn.title = reason;
    btn.classList.add('shop-btn-disabled');
    btn.style.opacity = '0.3';
    btn.style.pointerEvents = 'auto';
    btn.style.cursor = 'help';
    if (reason) {
        btn.addEventListener('click', () => events.emit(Events.NOTIFICATION_REQUEST, reason));
    }
}

/**
 * Audit D02: permanent-purchase confirm dialog. Tattoos, piercings, future
 * irreversible purchases route through this — friction before commit so a
 * single misclick can't burn $40-100 on a permanent body mod.
 * Returns Promise<boolean>.
 */
function _confirmPermanentPurchase({ title, itemName, location, price, warning, confirmLabel }) {
    return new Promise(resolve => {
        const overlay = document.createElement('div');
        overlay.className = 'shop-confirm-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(8,6,12,0.85);z-index:700;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(6px);';

        const dialog = document.createElement('div');
        dialog.className = 'shop-confirm-dialog';
        dialog.style.cssText = 'max-width:420px;width:90%;background:var(--bg-panel);border:1px solid var(--border-glow);border-radius:12px;padding:24px;color:var(--text);box-shadow:0 20px 60px rgba(0,0,0,0.6);';
        const safeName = String(itemName || '').replace(/[<>&"']/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'})[c]);
        const safeLoc = String(location || '').replace(/[<>&"']/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'})[c]);
        const safeWarning = String(warning || '').replace(/[<>&"']/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'})[c]);
        dialog.innerHTML = `
            <h3 style="margin:0 0 8px 0;color:var(--rose);font-size:18px;">${title}</h3>
            <p style="margin:0 0 12px 0;color:var(--text-body);font-size:14px;line-height:1.5;">
                <strong>${safeName}</strong>${safeLoc ? ' &middot; ' + safeLoc : ''} &middot; <strong style="color:var(--gold);">$${price}</strong>
            </p>
            <p style="margin:0 0 16px 0;color:#f0c0c0;font-size:13px;line-height:1.5;background:rgba(120,40,60,0.15);border-left:3px solid rgba(220,120,120,0.5);padding:10px 12px;border-radius:4px;">
                ${safeWarning}
            </p>
            <div style="display:flex;gap:12px;justify-content:flex-end;margin-top:20px;">
                <button class="shop-confirm-cancel" style="min-height:44px;padding:10px 18px;background:transparent;border:1px solid var(--border);border-radius:8px;color:var(--text-body);font-size:14px;cursor:pointer;">Cancel</button>
                <button class="shop-confirm-ok" style="min-height:44px;padding:10px 18px;background:rgba(196,80,80,0.25);border:1px solid rgba(220,120,120,0.65);border-radius:8px;color:#ffd0d0;font-size:14px;font-weight:600;cursor:pointer;">${confirmLabel || 'Confirm'}</button>
            </div>
        `;
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);

        const cleanup = (result) => {
            overlay.remove();
            document.removeEventListener('keydown', onKey);
            resolve(result);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') cleanup(false);
            else if (e.key === 'Enter') cleanup(true);
        };
        dialog.querySelector('.shop-confirm-cancel').addEventListener('click', () => cleanup(false));
        dialog.querySelector('.shop-confirm-ok').addEventListener('click', () => cleanup(true));
        overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(false); });
        document.addEventListener('keydown', onKey);
        setTimeout(() => dialog.querySelector('.shop-confirm-cancel')?.focus(), 50);
    });
}

// ── Shop type definitions ───────────────────────────────────────────────
const SHOP_TYPES = {
    [UITags.SHOP_LINGERIE]:  { nameKey: 'shop.type.lingerie',  fallback: 'Lingerie Store',      icon: '\uD83C\uDF80', color: '#e88cb8', img: 'icon_shop_lingerie', banner: 'banner_shop_lingerie.jpg' },
    [UITags.SHOP_ADULT]:     { nameKey: 'shop.type.adult',     fallback: 'Adult Shop',           icon: '\uD83D\uDD1E', color: '#d86050', img: 'icon_shop_adult',    banner: 'banner_shop_adult.jpg' },
    [UITags.SHOP_MENS]:      { nameKey: 'shop.type.mens',      fallback: "Men's Clothing",       icon: '\uD83D\uDC54', color: '#5888cc', img: 'icon_shop_mens',     banner: 'banner_shop_mens.jpg' },
    [UITags.SHOP_WOMENS]:    { nameKey: 'shop.type.womens',    fallback: "Women's Clothing",     icon: '\uD83D\uDC57', color: '#e88cb8', img: 'icon_shop_womens',   banner: 'banner_shop_womens.jpg' },
    [UITags.SHOP_PHARMACY]:  { nameKey: 'shop.type.pharmacy',  fallback: 'Pharmacy',             icon: '\uD83D\uDC8A', color: '#5cd89c', img: 'icon_shop_pharmacy', banner: 'banner_shop_pharmacy.jpg' },
    [UITags.SHOP_APARTMENT]: { nameKey: 'shop.type.apartment', fallback: 'Apartment Upgrades',   icon: '\uD83C\uDFE0', color: '#e8c860' },
    [UITags.SHOP_STREAM]:    { nameKey: 'shop.type.stream',    fallback: 'Stream Equipment',     icon: '\uD83D\uDCF9', color: '#e06878' },
    [UITags.SHOP_TATTOO]:    { nameKey: 'shop.type.tattoo',    fallback: 'Inkwell Studio',       icon: '\uD83D\uDD89', color: '#b060d8', banner: 'banner_shop_tattoo.jpg' },
    // 0.3 intimate piercings (INTIMATE_PIERCINGS.md): generic items.json
    // renderer on purpose \u2014 must NOT join the isWebshop list or the
    // _renderTattooShop branch. Reuses the tattoo banner (same venue).
    [UITags.SHOP_TATTOO_BACKROOM]: { nameKey: 'shop.type.tattoo_backroom', fallback: 'Back Room, Inkwell', icon: '\uD83D\uDC89', color: '#b060d8', banner: 'banner_shop_tattoo.jpg' },
    [UITags.SHOP_FETISH]:    { nameKey: 'shop.type.fetish',    fallback: 'Kitten & Chrome',      icon: '🔓', color: '#a04090', banner: 'banner_shop_fetish.jpg' },
};

// Apartment upgrade items (not in items.json — tier-based, sets Ink vars directly)
const APARTMENT_UPGRADES = [
    { id: 'upgrade_bed', nameKey: 'apt.bed.name', fallbackName: 'Bed', tiers: [
        { tier: 1, nameKey: 'apt.bed.t1.name', fallbackName: 'Foam Mattress', price: 150, descKey: 'apt.bed.t1.desc', fallbackDesc: 'Max Energy +15 (→115)', icon: '\uD83D\uDECF\uFE0F' },
        { tier: 2, nameKey: 'apt.bed.t2.name', fallbackName: 'Queen Bed', price: 350, descKey: 'apt.bed.t2.desc', fallbackDesc: 'Max Energy +20 (→120)', icon: '\uD83D\uDECF\uFE0F' },
        { tier: 3, nameKey: 'apt.bed.t3.name', fallbackName: 'Luxury Bed', price: 600, descKey: 'apt.bed.t3.desc', fallbackDesc: 'Max Energy +25 (→125)', icon: '\uD83D\uDECF\uFE0F' },
    ]},
    { id: 'upgrade_desk', nameKey: 'apt.desk.name', fallbackName: 'Desk', tiers: [
        { tier: 1, nameKey: 'apt.desk.t1.name', fallbackName: 'Basic Chair', price: 200, descKey: 'apt.desk.t1.desc', fallbackDesc: '+2 starting viewers', icon: '\uD83E\uDE91' },
        { tier: 2, nameKey: 'apt.desk.t2.name', fallbackName: 'Gaming Setup', price: 400, descKey: 'apt.desk.t2.desc', fallbackDesc: '+5 starting viewers, bonus tips', icon: '\uD83E\uDE91' },
        { tier: 3, nameKey: 'apt.desk.t3.name', fallbackName: 'Pro Studio', price: 700, descKey: 'apt.desk.t3.desc', fallbackDesc: '+8 starting viewers, extra round', icon: '\uD83E\uDE91' },
    ]},
    { id: 'upgrade_vanity', nameKey: 'apt.vanity.name', fallbackName: 'Vanity', tiers: [
        { tier: 1, nameKey: 'apt.vanity.t1.name', fallbackName: 'Makeup Mirror', price: 175, descKey: 'apt.vanity.t1.desc', fallbackDesc: 'Morning mirror events', icon: '\uD83E\uDE9E' },
        { tier: 2, nameKey: 'apt.vanity.t2.name', fallbackName: 'Lit Mirror', price: 375, descKey: 'apt.vanity.t2.desc', fallbackDesc: 'Relief Fem bonus, wardrobe bonus', icon: '\uD83E\uDE9E' },
        { tier: 3, nameKey: 'apt.vanity.t3.name', fallbackName: 'Dressing Table', price: 650, descKey: 'apt.vanity.t3.desc', fallbackDesc: 'Practice Posing + Hair Touch-Up + -5 Energy on every vanity action', icon: '\uD83E\uDE9E' },
    ]},
    { id: 'upgrade_decor', nameKey: 'apt.decor.name', fallbackName: 'Decor', tiers: [
        { tier: 1, nameKey: 'apt.decor.t1.name', fallbackName: 'LED Lights', price: 100, descKey: 'apt.decor.t1.desc', fallbackDesc: 'Shame decay at 75+', icon: '\uD83C\uDF1F' },
        { tier: 2, nameKey: 'apt.decor.t2.name', fallbackName: 'Cozy Setup', price: 275, descKey: 'apt.decor.t2.desc', fallbackDesc: 'Shame decay at 50+', icon: '\uD83C\uDF1F' },
        { tier: 3, nameKey: 'apt.decor.t3.name', fallbackName: 'Sanctuary', price: 500, descKey: 'apt.decor.t3.desc', fallbackDesc: 'Shame decay at 25+, Max Energy +5', icon: '\uD83C\uDF1F' },
    ]},
    { id: 'upgrade_coffee', nameKey: 'apt.coffee.name', fallbackName: 'Kitchen', tiers: [
        { tier: 1, nameKey: 'apt.coffee.t1.name', fallbackName: 'Espresso Machine', price: 400, descKey: 'apt.coffee.t1.desc', fallbackDesc: '+15 Energy once per day (fresh espresso at home)', icon: '\u2615' },
    ]},
];

// Apartment Decor (boolean Ink vars, one-time purchase). Each contributes 1
// nest-tier point. Fem/Sub-gated by `req` (visibility) + `patreonOnly`. Sets
// the matching `decor_*` Ink VAR + triggers recompute_nest_tier() on buy.
// Image generated for each tag → assets/images/Items/<img>.png.
const APARTMENT_DECOR = [
    { inkVar: 'decor_pink_curtains',      nameKey: 'apt.decor_curtains.name',     fallbackName: 'Pink Velvet Curtains', price: 120, descKey: 'apt.decor_curtains.desc',     fallbackDesc: 'Pink touches that soften the bedroom.',         icon: '🎀', img: 'decor_pink_curtains',      req: {Femininity: 25} },
    { inkVar: 'decor_pink_bedsheets',     nameKey: 'apt.decor_sheets.name',       fallbackName: 'Pink Satin Bedsheets', price: 80,  descKey: 'apt.decor_sheets.desc',       fallbackDesc: 'Cool satin against your skin every night.',     icon: '🛏️', img: 'decor_pink_bedsheets',     req: {Femininity: 30} },
    // Vanity slot is intentionally covered by upgrade_vanity (existing tier-upgrade
    // above). Owning upgrade_vanity >= 1 contributes to apartment_nest_tier via
    // recompute_nest_tier() — no separate decor entry needed.
    { inkVar: 'decor_full_length_mirror', nameKey: 'apt.decor_mirror.name',       fallbackName: 'Full-Length Mirror',   price: 300, descKey: 'apt.decor_mirror.desc',       fallbackDesc: 'See yourself, head to toe, in private.',        icon: '🪞', img: 'decor_full_length_mirror', req: {Femininity: 50} },
    { inkVar: 'decor_collar_display',     nameKey: 'apt.decor_collar.name',       fallbackName: 'Collar Display',       price: 180, descKey: 'apt.decor_collar.desc',       fallbackDesc: 'A small velvet stand. Your collar at night.',   icon: '⚓',     img: 'decor_collar_display',     req: {Submissiveness: 50} },
    { inkVar: 'decor_sissy_wall_art',     nameKey: 'apt.decor_wallart.name',      fallbackName: 'Sissy Wall Art',       price: 200, descKey: 'apt.decor_wallart.desc',      fallbackDesc: 'Framed prints that leave no doubt.',            icon: '🖼️', img: 'decor_sissy_wall_art', req: {Femininity: 60} },
    { inkVar: 'decor_master_photos',      nameKey: 'apt.decor_master.name',       fallbackName: 'Master Photos',        price: 50,  descKey: 'apt.decor_master.desc',       fallbackDesc: 'A small frame on the bedside table.',           icon: '🖼️', img: 'decor_master_photos',      req: {MasterRelationship: 5} },
];

// Apartment Expansions (paid unlocks: basement, playroom, playroom-furniture).
// Same boolean pattern. Basement gated on Day >= 15 (player has cash flow).
// Playroom conversion gated on apartment_nest_tier >= 2 (nest is established).
// Equipment Rack gated on apartment_has_playroom (no playroom = nowhere to put it).
const APARTMENT_EXPANSIONS = [
    { inkVar: 'apartment_has_basement',       nameKey: 'apt.expand_basement.name',  fallbackName: 'Basement Lease',         price: 800,  descKey: 'apt.expand_basement.desc',  fallbackDesc: 'Lease the basement below. Workshop. Storage. Privacy.', icon: '🔒', img: 'apartment_basement_lease',       req: {Day: 15} },
    { inkVar: 'apartment_has_playroom',       nameKey: 'apt.expand_playroom.name',  fallbackName: 'Spare Room Conversion',  price: 1200, descKey: 'apt.expand_playroom.desc',  fallbackDesc: 'Convert the spare room. Mirrors, equipment, mounted rings.', icon: '🚪', img: 'apartment_playroom_conversion',  req: {apartment_nest_tier: 2} },
    { inkVar: 'furniture_equipment_rack',     nameKey: 'apt.expand_rack.name',      fallbackName: 'Mounted Equipment Rack', price: 400,  descKey: 'apt.expand_rack.desc',      fallbackDesc: 'A wall-mounted rack: cuffs, paddles, the works.',       icon: '🪝', img: 'furniture_equipment_rack',       req: {apartment_has_playroom: true} },
];

// Stream equipment items (boolean Ink vars, one-time purchase)
const STREAM_EQUIPMENT = [
    { inkVar: 'perk_ring_light', nameKey: 'stream.ring_light.name', fallbackName: 'Ring Light', price: 150, descKey: 'stream.ring_light.desc', fallbackDesc: '+5-10 bonus viewers', icon: '\uD83D\uDCA1', img: 'shop_ring_light' },
    { inkVar: 'perk_cute_bg', nameKey: 'stream.cute_bg.name', fallbackName: 'Cute Background', price: 200, descKey: 'stream.cute_bg.desc', fallbackDesc: '+$5-15 extra tips/round', icon: '\uD83C\uDF08', img: 'shop_cute_background' },
    { inkVar: 'perk_second_cam', nameKey: 'stream.second_cam.name', fallbackName: 'Second Camera', price: 180, descKey: 'stream.second_cam.desc', fallbackDesc: '$10-20 bonus on physical actions', icon: '\uD83D\uDCF7', img: 'shop_second_camera' },
    { inkVar: 'perk_good_mic', nameKey: 'stream.good_mic.name', fallbackName: 'Good Microphone', price: 200, descKey: 'stream.good_mic.desc', fallbackDesc: 'Unlocks ASMR action', icon: '\uD83C\uDF99\uFE0F', img: 'shop_good_mic' },
    { inkVar: 'perk_chat_bot', nameKey: 'stream.chat_bot.name', fallbackName: 'Chat Bot Pro', price: 300, descKey: 'stream.chat_bot.desc', fallbackDesc: 'Auto-ban 50% trolls', icon: '\uD83E\uDD16', img: 'shop_chat_bot' },
    { inkVar: 'perk_lovense', nameKey: 'stream.lovense.name', fallbackName: 'Lovense Toy', price: 250, descKey: 'stream.lovense.desc', fallbackDesc: 'Unlocks Interactive Toy action (Fem 25+)', icon: '\uD83D\uDC9C', img: 'shop_lovense' },
];

// Tattoo designs (permanent one-time, sets Ink flags + applies stat changes).
// Tattoos are rendered via _tatN(...) helpers that wrap name/desc/location in
// L.get() with the EN literal as fallback. Keys: shop.tattoo.<inkVar>.name|desc|location
const TATTOO_DESIGNS = [
    { category: 'neutral', inkVar: 'has_tattoo_star', name: 'Small Star', location: 'Wrist', price: 40, desc: 'Subtle confidence. +5 Energy overnight.', img: 'tattoo_star', energy: 15, statChanges: [] },
    { category: 'neutral', inkVar: 'has_tattoo_abstract', name: 'Abstract Lines', location: 'Forearm', price: 80, desc: 'Self-expression. -1 Shame overnight.', img: 'tattoo_abstract', energy: 15, statChanges: [] },
    { category: 'feminine', inkVar: 'has_tattoo_rose', name: 'Rose Vine', location: 'Ankle', price: 60, desc: 'Delicate. +1 Stream Allure, +2 Fem.', img: 'tattoo_rose', energy: 15, femReq: 15, statChanges: [{fn:'fem_gain', val:2}] },
    { category: 'feminine', inkVar: 'has_tattoo_butterfly', name: 'Butterfly', location: 'Lower Back', price: 80, desc: 'Bold statement. +2 Stream Allure, +3 Fem, +3 Shame.', img: 'tattoo_butterfly', energy: 20, femReq: 20, statChanges: [{fn:'fem_gain', val:3}, {fn:'shame_gain', val:3}] },
    { category: 'feminine', inkVar: 'has_tattoo_heart', name: 'Heart', location: 'Hip', price: 100, desc: 'For anyone who gets close. +2 Stream Allure, +3 Fem, +2 Sub.', img: 'tattoo_heart', energy: 20, femReq: 30, statChanges: [{fn:'fem_gain', val:3}, {fn:'sub_gain', val:2}] },
    { category: 'masculine', inkVar: 'has_tattoo_tribal', name: 'Tribal Band', location: 'Upper Arm', price: 70, desc: 'Grounding. -1 Shame overnight, -2 Fem.', img: 'tattoo_tribal', energy: 15, femMax: 30, statChanges: [{stat:'Femininity', val:-2}] },
    { category: 'masculine', inkVar: 'has_tattoo_wolf', name: 'Wolf', location: 'Shoulder', price: 100, desc: 'Fierce. +5 Energy, -1 Shame overnight, -3 Fem.', img: 'tattoo_wolf', energy: 20, femMax: 25, statChanges: [{stat:'Femininity', val:-3}] },
];

// Tattoo localization helpers (used by render site + confirm dialog)
function _tatName(t) { return L.get(`shop.tattoo.${t.inkVar}.name`, t.name); }
function _tatDesc(t) { return L.get(`shop.tattoo.${t.inkVar}.desc`, t.desc); }
function _tatLocation(t) { return L.get(`shop.tattoo.${t.inkVar}.location`, t.location); }

class ShopUI {
    constructor() {
        /** @type {HTMLElement|null} */
        this._root = null;

        /** @type {boolean} */
        this._isOpen = false;

        /** @type {string|null} Active shop type tag */
        this._shopType = null;

        /** @type {Array<object>} Current items list */
        this._items = [];

        /** @type {Set<string>} Owned item tags */
        this._ownedItems = new Set();

        /** @type {number} Player money (mirrors Ink, synced on shop open) */
        this._money = 0;

        this._scope = events.createScope('shop');
        this._scope.on(Events.FORCED_EVENT_STARTED, () => this._close());
        this._bindEvents();
    }

    /** Detach all event listeners (tests + future re-init paths). */
    destroy() {
        this._scope.destroy();
    }

    // ── Public API ──────────────────────────────────────────────────────

    init() {
        this._root = document.getElementById('overlay-shop');
        if (!this._root) {
            this._root = this._createContainer();
        }
    }

    /**
     * Register items available in shops.
     * Called by systems-dev's inventory module with full item data.
     * @param {Array<object>} items - [{tag, name, price, slot, buffs, description, shopType, icon}]
     */
    registerItems(items) {
        this._items = items;
    }

    /**
     * Update the set of owned item tags.
     * @param {Set<string>|Array<string>} owned
     */
    updateOwned(owned) {
        this._ownedItems = owned instanceof Set ? owned : new Set(owned);
        if (this._isOpen) this._renderItems();
    }

    /** Check if shop is active. */
    isOpen() {
        return this._isOpen;
    }

    // ── Event Bindings ──────────────────────────────────────────────────

    _bindEvents() {
        this._scope.on(Events.TAG_UI, (mode) => {
            // Check if it's a shop_ prefixed tag
            if (mode && mode.startsWith('shop_') && SHOP_TYPES[mode]) {
                this._openShop(mode);
            } else if (this._isOpen) {
                // Any non-shop ui tag closes the shop
                this._close();
            }
        });

        this._scope.on(Events.STAT_CHANGED, (statName, newVal) => {
            if (statName === 'Money') {
                this._money = newVal;
                this._updateMoneyDisplay();
            }
        });

        this._scope.on(Events.ITEM_ADDED, (item) => {
            if (item && item.tag) {
                this._ownedItems.add(item.tag);
                if (this._isOpen) this._renderItems();
            }
        });
    }

    // ── Shop Operations ─────────────────────────────────────────────────

    _openShop(shopType) {
        if (!this._root) this.init();
        this._shopType = shopType;
        this._activeFilter = null;
        this._isOpen = true;

        // Sync owned items from inventory before rendering
        if (this._inventoryRef) {
            const owned = this._inventoryRef.getOwnedTags?.() || [];
            this._ownedItems = new Set(owned);
        }

        // Sync current money from variableManager (canonical source, always in sync with sidebar)
        this._money = variableManager.getTotalValue('Money') || 0;

        this._root.classList.remove('hidden');
        this._buildShopUI();
        this._renderItems();
        events.emit(Events.OVERLAY_OPEN, 'overlay-shop');
    }

    /**
     * Set reference to inventory manager for owned-item sync.
     * @param {object} inv - inventoryManager instance
     */
    setInventoryRef(inv) {
        this._inventoryRef = inv;
    }

    _close() {
        if (!this._isOpen) return;
        this._isOpen = false;
        this._shopType = null;
        // Clean up any orphaned tooltips appended to document.body
        document.querySelectorAll('.shop-preview-tooltip').forEach(el => el.remove());
        // Let layout.closeOverlay handle hiding + backdrop removal
        // (it checks for 'hidden' and bails if already set, so we must NOT add hidden first)
        events.emit(Events.OVERLAY_CLOSE, 'overlay-shop');
    }

    // ── DOM Construction ────────────────────────────────────────────────

    _buildShopUI() {
        const raw = SHOP_TYPES[this._shopType] || { nameKey: '', fallback: 'Shop', icon: '\uD83D\uDED2', color: '#887ca0' };
        const info = { ...raw, name: (raw.nameKey ? L.get(raw.nameKey) : null) || raw.fallback || 'Shop' };

        // Build inside .overlay-box, not the overlay root (preserves background styling)
        let box = this._root.querySelector('.overlay-box');
        if (!box) {
            box = document.createElement('div');
            box.className = 'overlay-box shop-box';
            this._root.innerHTML = '';
            this._root.appendChild(box);
        }
        box.innerHTML = '';

        const frame = document.createElement('div');
        frame.className = 'shop-frame';
        frame.style.setProperty('--shop-color', info.color);

        // Storefront banner (for shops with generated banners)
        if (info.banner) {
            const storefront = document.createElement('div');
            storefront.className = 'shop-storefront';
            storefront.innerHTML = `
                <img src="assets/images/UI/${info.banner}" alt="${info.name}">
                <div class="shop-storefront-overlay">
                    <span class="shop-storefront-title">${info.name}</span>
                </div>
            `;
            frame.appendChild(storefront);
        }

        // Header — webshop types (apartment/stream) have their own banner, only show money
        const isWebshop = this._shopType === UITags.SHOP_APARTMENT || this._shopType === UITags.SHOP_STREAM || this._shopType === UITags.SHOP_TATTOO;
        const header = document.createElement('div');
        header.className = 'shop-header' + (info.banner ? ' shop-header-compact' : '') + (isWebshop ? ' shop-header-minimal' : '');
        if (isWebshop) {
            header.innerHTML = `<span class="shop-money" id="shop-money">$${this._money}</span>`;
        } else {
            const headerIcon = info.img
                ? `<img src="assets/images/UI/${info.img}.png" alt="${info.name}">`
                : info.icon;
            header.innerHTML = `
                <span class="shop-header-icon">${headerIcon}</span>
                <h3 class="shop-header-name">${info.banner ? '' : info.name}</h3>
                <span class="shop-money" id="shop-money">$${this._money}</span>
            `;
        }
        frame.appendChild(header);

        // Floating close button (top-right)
        const closeBtn = document.createElement('button');
        closeBtn.className = 'overlay-close-float';
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('click', () => this._close());
        frame.appendChild(closeBtn);

        // Items container
        const items = document.createElement('div');
        items.className = 'shop-items';
        items.id = 'shop-items';
        frame.appendChild(items);

        box.appendChild(frame);
    }

    /** @param {Function|null} storyReader - () => story for reading Ink vars */
    setStoryReader(reader) { this._storyReader = reader; }

    _renderItems() {
        const container = this._root ? this._root.querySelector('#shop-items') : null;
        if (!container) return;
        container.innerHTML = '';
        // Tooltips are appended to document.body, not into container, so a
        // re-render (filter tab) won't clear a tooltip whose mouseleave never
        // fired because its card was wiped. Sweep orphans here too.
        document.querySelectorAll('.shop-preview-tooltip').forEach(el => el.remove());

        // Special shops: apartment upgrades and stream equipment
        if (this._shopType === UITags.SHOP_APARTMENT) {
            this._renderApartmentUpgrades(container);
            return;
        }
        if (this._shopType === UITags.SHOP_STREAM) {
            this._renderStreamEquipment(container);
            return;
        }
        if (this._shopType === UITags.SHOP_TATTOO) {
            this._renderTattooShop(container);
            return;
        }

        // Filter items by shop type, exclude $0 starter items
        let filtered = this._items.filter(item => item.shopType === this._shopType && (item.price || 0) > 0);

        // Slot filter tabs
        const slots = [...new Set(filtered.map(i => EquipSlotNames[i.slot]).filter(Boolean))];
        if (slots.length > 1) {
            const filterBar = document.createElement('div');
            filterBar.className = 'shop-filters';

            const allTab = document.createElement('span');
            allTab.className = 'shop-filter-tab active';
            allTab.textContent = L.get('shop.filter_all') || 'All';
            allTab.addEventListener('click', () => {
                this._activeFilter = null;
                this._renderItems();
            });
            filterBar.appendChild(allTab);

            for (const slot of slots) {
                const tab = document.createElement('span');
                tab.className = 'shop-filter-tab' + (this._activeFilter === slot ? ' active' : '');
                tab.textContent = (slot ? L.get('slot.' + slot.toLowerCase()) : '') || slot;
                tab.addEventListener('click', () => {
                    this._activeFilter = slot;
                    this._renderItems();
                });
                filterBar.appendChild(tab);
            }
            // Mark active correctly
            if (this._activeFilter) allTab.classList.remove('active');
            container.appendChild(filterBar);
        }

        // Apply slot filter
        if (this._activeFilter) {
            filtered = filtered.filter(i => EquipSlotNames[i.slot] === this._activeFilter);
        }

        // Sort: available first, then owned, then locked
        filtered.sort((a, b) => {
            const aOwned = this._ownedItems.has(a.tag) ? 1 : 0;
            const bOwned = this._ownedItems.has(b.tag) ? 1 : 0;
            return aOwned - bOwned;
        });

        if (filtered.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'shop-empty';
            empty.textContent = L.get('shop.empty') || 'No items available.';
            container.appendChild(empty);
            return;
        }

        for (const item of filtered) {
            const isOwned = this._ownedItems.has(item.tag);
            const canAfford = this._money >= (item.price || 0);

            // Requirement gate check
            let isLocked = false;
            let requirementLabel = '';
            if (item.requiredInkVariable && !isOwned) {
                const story = this._storyReader?.();
                const currentVal = story ? (story.variablesState[item.requiredInkVariable] ?? 0) : 0;
                const threshold = item.requiredThreshold;
                if (threshold !== undefined && threshold !== null) {
                    // Numeric threshold: value must meet or exceed it
                    if (currentVal < threshold) {
                        isLocked = true;
                        // Fem-tier specific hints
                        if (item.requiredInkVariable === 'Femininity') {
                            if (threshold === 15) requirementLabel = L.get('shop.req_fem_1') || 'Requires Fem Tier 1 (15+)';
                            else if (threshold === 30) requirementLabel = L.get('shop.req_fem_2') || 'Requires Fem Tier 2 (30+)';
                            else if (threshold === 50) requirementLabel = L.get('shop.req_fem_3') || 'Requires Fem Tier 3 (50+)';
                            else requirementLabel = (L.get('shop.req_fem_n') || 'Requires Fem {N}+').replace('{N}', threshold);
                        } else if (item.requiredInkVariable === 'dildo_home_uses' || item.requiredInkVariable === 'plug_home_uses') {
                            // Toy size-up: "break in" the current size before the next.
                            requirementLabel = (L.get('shop.locked_use_more') || 'Use your current size a few more times ({cur}/{N})').replace('{cur}', currentVal).replace('{N}', threshold);
                        } else {
                            requirementLabel = (L.get('shop.req_var') || 'Requires {var} {N}+').replace('{var}', item.requiredInkVariable).replace('{N}', threshold);
                        }
                    }
                } else {
                    // Boolean gate: variable must be truthy
                    // Low-intensity (0=Independent, 1=Roommate): bypass master_order_* and master_reward gates
                    const masterInt = story ? (story.variablesState['master_intensity'] ?? 2) : 2;
                    const isLowIntensity = masterInt <= 1;
                    const isMasterGate = item.requiredInkVariable?.startsWith('master_order_') || item.requiredInkVariable === 'master_reward_given';
                    // Real-time stat check for chastity to avoid self-contradicting
                    // labels when Fem/Sub thresholds are crossed mid-day. The Ink flag
                    // only flips during overnight_processing.
                    // - Indie/Roommate (low intensity): Fem >= 25
                    // - Landlord/Owner (no Master order yet): Fem >= 30 + Sub >= 30 (preempt)
                    // Purchasing sets chastity_unlocked=true via setsOnPurchase.
                    const fem = story ? (story.variablesState['Femininity'] ?? 0) : 0;
                    const sub = story ? (story.variablesState['Submissiveness'] ?? 0) : 0;
                    const masterOrderChastity = story ? (story.variablesState['master_order_chastity'] ?? 0) : 0;
                    const isChastityFemUnlocked = item.requiredInkVariable === 'chastity_unlocked' && isLowIntensity && fem >= 25;
                    const isChastityMasterPreempt = item.requiredInkVariable === 'chastity_unlocked' && masterInt >= 2 && masterOrderChastity === 0 && fem >= 30 && sub >= 30;
                    if (!currentVal && !(isLowIntensity && isMasterGate) && !isChastityFemUnlocked && !isChastityMasterPreempt) {
                        isLocked = true;
                        // Context-aware lock reason: give the player a clear "why"
                        // instead of a silent "Locked" stamp.
                        const gateVar = item.requiredInkVariable;
                        if (gateVar === 'chastity_unlocked') {
                            // Landlord/Owner: either pre-stat-gate (show stat hint) or post-order-given.
                            // Indie/Roommate: pre-Fem-25.
                            if (masterInt >= 2 && masterOrderChastity === 0) {
                                requirementLabel = (L.get('shop.locked_chastity_master_preempt') || 'Order awaits, or unlocks at Fem 30 + Sub 30 ({fem}/30, {sub}/30)').replace('{fem}', fem).replace('{sub}', sub);
                            } else if (masterInt >= 2) {
                                requirementLabel = L.get('shop.locked_master_order') || 'Master hasn\u2019t ordered this yet';
                            } else {
                                requirementLabel = (L.get('shop.locked_chastity_fem') || 'Unlocks at Femininity 25 ({cur}/25)').replace('{cur}', fem);
                            }
                        } else if (gateVar?.startsWith('master_order_')) {
                            // Landlord/Owner pre-order gate on other items (plug, etc.)
                            requirementLabel = L.get('shop.locked_master_order') || 'Master hasn\u2019t ordered this yet';
                        } else if (gateVar === 'master_reward_given') {
                            requirementLabel = L.get('shop.locked_master_reward') || 'Unlocks after a Master reward';
                        } else if (gateVar === 'owns_plug' || gateVar === 'owns_plug_medium' || gateVar === 'owns_dildo' || gateVar === 'owns_dildo_medium') {
                            // Tier upgrade: need the previous size first
                            requirementLabel = L.get('shop.locked_prev_tier') || 'Buy the previous size first';
                        } else if (gateVar?.startsWith('bondage_stage_')) {
                            requirementLabel = L.get('shop.locked_kennel') || 'Train with Mira at the Kennel';
                        } else {
                            requirementLabel = L.get('shop.locked') || 'Locked';
                        }
                    }
                }
            }

            // Compound prerequisite: an extra "must also own" var (independent of
            // the threshold gate above). Used for tiered toys so the large needs
            // BOTH the use-count (requiredInkVariable/threshold) AND ownership of
            // the previous size (requiredAlsoOwned) — prevents skipping medium.
            if (!isOwned && item.requiredAlsoOwned) {
                const story = this._storyReader?.();
                const ownsPrev = story ? !!(story.variablesState[item.requiredAlsoOwned]) : false;
                if (!ownsPrev) {
                    isLocked = true;
                    // Context-aware label: the generic text assumes the tiered-toy
                    // case ("previous size"), which is wrong for state gates like
                    // the cage piercing (0.3, INTIMATE_PIERCINGS.md).
                    if (item.requiredAlsoOwned === 'wearing_chastity') {
                        requirementLabel = L.get('shop.locked_cage_on') || 'Requires your cage on';
                    } else {
                        requirementLabel = L.get('shop.locked_prev_tier') || 'Buy the previous size first';
                    }
                }
            }

            const card = document.createElement('div');
            card.className = 'shop-item-card' + (isOwned ? ' shop-item-owned' : '') + (isLocked ? ' shop-item-locked' : '');

            // Icon — use tag as image filename (all items have PNGs in Items/)
            const iconArea = document.createElement('div');
            iconArea.className = 'shop-item-icon';
            const img = document.createElement('img');
            const _ri = window._pno_resolveImage;
            img.src = (_ri ? _ri('Items', item.icon || item.tag) : null) || `assets/images/Items/${item.icon || item.tag}.png`;
            img.alt = L.getItem(item.tag, 'name', item.displayName || item.name || item.tag);
            img.loading = 'lazy';
            img.onerror = () => { img.style.display = 'none'; iconArea.textContent = '\uD83D\uDC5A'; };
            iconArea.appendChild(img);
            // Hover preview tooltip (larger image)
            let preview = null;
            iconArea.addEventListener('mouseenter', () => {
                preview = document.createElement('div');
                preview.className = 'shop-preview-tooltip';
                preview.innerHTML = `<img src="${img.src}" alt="${L.getItem(item.tag, 'name', item.displayName || item.tag)}" onerror="this.style.display='none'">`;
                document.body.appendChild(preview);
                const rect = iconArea.getBoundingClientRect();
                preview.style.left = `${rect.right + 10}px`;
                preview.style.top = `${rect.top - 20}px`;
                // Clamp to viewport
                const pr = preview.getBoundingClientRect();
                if (pr.right > window.innerWidth - 8) preview.style.left = `${rect.left - pr.width - 10}px`;
                if (pr.bottom > window.innerHeight - 8) preview.style.top = `${window.innerHeight - pr.height - 8}px`;
            });
            iconArea.addEventListener('mouseleave', () => { if (preview) { preview.remove(); preview = null; } });
            card.appendChild(iconArea);

            // Info
            const info = document.createElement('div');
            info.className = 'shop-item-info';

            const nameEl = document.createElement('div');
            nameEl.className = 'shop-item-name';
            nameEl.textContent = L.getItem(item.tag, 'name', item.displayName || item.name);
            info.appendChild(nameEl);

            // Slot
            if (item.slot !== undefined && item.slot !== null) {
                const slotEl = document.createElement('div');
                slotEl.className = 'shop-item-slot';
                const slotName = EquipSlotNames[item.slot] || '';
                slotEl.textContent = (slotName ? L.get('slot.' + slotName.toLowerCase()) : '') || slotName;
                info.appendChild(slotEl);
            }

            // Buffs — handle both array [{stat, value}] and object {stat: value} formats
            const buffs = Array.isArray(item.buffs)
                ? item.buffs
                : (item.buffs && typeof item.buffs === 'object')
                    ? Object.entries(item.buffs).map(([stat, value]) => ({ stat, value }))
                    : [];
            if (buffs.length > 0) {
                const buffsEl = document.createElement('div');
                buffsEl.className = 'shop-item-buffs';
                for (const b of buffs) {
                    const badge = document.createElement('span');
                    badge.className = `shop-buff-badge shop-buff-${b.stat.toLowerCase()}`;
                    badge.textContent = `${b.value > 0 ? '+' : ''}${b.value} ${b.stat}`;
                    buffsEl.appendChild(badge);
                }
                info.appendChild(buffsEl);
            }

            // Description
            if (item.description) {
                const desc = document.createElement('div');
                desc.className = 'shop-item-desc';
                desc.textContent = L.getItem(item.tag, 'desc', item.description);
                info.appendChild(desc);
            }

            card.appendChild(info);

            // Price + Buy button
            const actions = document.createElement('div');
            actions.className = 'shop-item-actions';

            if (isOwned) {
                const ownedLabel = document.createElement('span');
                ownedLabel.className = 'shop-item-owned-label';
                ownedLabel.textContent = L.get('shop.owned') || 'Owned';
                actions.appendChild(ownedLabel);

                // Try On / Equip button
                const equipBtn = document.createElement('button');
                equipBtn.className = 'shop-btn shop-btn-equip';
                if (inventoryManager.isMascClothingFemLocked(item.tag)) {
                    // Mirror the wardrobe: boy clothes lock above the Fem cap.
                    // The shop:equip handler already blocks the equip; show WHY
                    // instead of a working-looking button that silently no-ops.
                    _markShopLockedBtn(equipBtn, L.get('wardrobe.too_fem'), L.get('wardrobe.too_fem_reason'));
                } else {
                    equipBtn.textContent = L.get('shop.equip') || 'Equip';
                    equipBtn.addEventListener('click', () => {
                        events.emit('shop:equip', item.tag);
                        events.emit(Events.AUDIO_SFX_PLAY, 'click1');
                    });
                }
                actions.appendChild(equipBtn);
            } else if (isLocked) {
                const lockLabel = document.createElement('span');
                lockLabel.className = 'shop-item-req-label';
                lockLabel.textContent = requirementLabel;
                actions.appendChild(lockLabel);

                const lockedBtn = document.createElement('button');
                lockedBtn.className = 'shop-btn shop-btn-buy shop-btn-disabled';
                lockedBtn.textContent = '\uD83D\uDD12 ' + (L.get('shop.locked') || 'Locked');
                lockedBtn.disabled = true;
                actions.appendChild(lockedBtn);
            } else {
                // Check for shop discount perk
                const hasDiscount = this._storyReader ? (() => {
                    try { const s = this._storyReader(); return s && !!s.variablesState['perk_shop_discount']; } catch(e) { return false; }
                })() : false;
                const originalPrice = item.price || 0;
                const finalPrice = hasDiscount ? Math.floor(originalPrice * 0.9) : originalPrice;

                const price = document.createElement('span');
                price.className = 'shop-item-price';
                if (hasDiscount && originalPrice > 0) {
                    price.innerHTML = `<span style="text-decoration:line-through;color:var(--text-muted);margin-right:4px">$${originalPrice}</span><span style="color:#5cd89c">$${finalPrice}</span>`;
                } else {
                    price.textContent = `$${originalPrice}`;
                }
                actions.appendChild(price);

                const canAffordFinal = this._money >= finalPrice;
                const buyBtn = document.createElement('button');
                buyBtn.className = 'shop-btn shop-btn-buy' + (canAffordFinal ? '' : ' shop-btn-disabled');
                buyBtn.textContent = canAffordFinal ? (L.get('shop.buy') || 'Buy') : (L.get('shop.not_enough') || 'Not enough');
                buyBtn.disabled = !canAffordFinal;
                buyBtn.addEventListener('click', async () => {
                    if (!canAffordFinal) return;
                    // Audit D02 (extended per user 2026-05-11): Piercings carry
                    // the same confirm-friction as tattoos. Piercings are
                    // technically un-equippable but the player still paid $80
                    // and the visible-cue cost on Day-1 is meaningful enough
                    // to warrant a confirm. Body-mod items get the dialog.
                    if (item.slot === 'Piercing') {
                        const proceed = await _confirmPermanentPurchase({
                            title: L.get('shop.piercing.confirm_title', 'Body Piercing'),
                            itemName: L.getItem(item.tag, 'name', item.displayName || item.name || item.tag),
                            location: '',
                            price: finalPrice,
                            warning: L.get('shop.piercing.confirm_warning', 'Piercings stay in your inventory once bought. Even if you take them out later, the holes leave visible marks on your skin in NPC scenes.'),
                            confirmLabel: L.get('shop.piercing.confirm_label', 'Get pierced'),
                        });
                        if (!proceed) return;
                        // Re-check money after the await.
                        const moneyNow = variableManager.getTotalValue('Money') || 0;
                        if (moneyNow < finalPrice) return;
                    }
                    this._purchaseItem(item, finalPrice);
                });
                actions.appendChild(buyBtn);
            }

            card.appendChild(actions);
            container.appendChild(card);
        }
    }

    _purchaseItem(item, overridePrice) {
        const price = overridePrice !== undefined ? overridePrice : (item.price || 0);
        // Emit purchase event for systems-dev's inventory to handle
        events.emit('shop:purchase', {
            tag: item.tag,
            price: price,
            name: item.name,
        });

        // Optimistic UI update
        events.emit(Events.AUDIO_SFX_PLAY, 'item_purchase1');
        this._ownedItems.add(item.tag);
        // Re-read the authoritative wallet instead of subtracting again: the
        // shop:purchase emit above ran synchronously through the deduction, and
        // the STAT_CHANGED observer (_scope.on Money) already set this._money to
        // the correct post-purchase value. A manual `-= price` here double-counts
        // the deduction in the display mirror (player saw "pay more than price").
        // Matches the apartment/stream/tattoo/boolean purchase paths.
        this._money = variableManager.getTotalValue('Money') || 0;
        this._renderItems();
        this._updateMoneyDisplay();
        statsUI.showToast(`Added: ${L.getItem(item.tag, 'name', item.displayName || item.name || item.tag)}`, 'notif-money');
    }

    _updateMoneyDisplay() {
        const el = this._root ? this._root.querySelector('#shop-money') : null;
        if (el) el.textContent = `$${this._money}`;
    }

    // Amazon-style apartment upgrade shop
    _renderApartmentUpgrades(container) {
        const story = this._storyReader?.();

        // Amazon-style dark header
        const header = document.createElement('div');
        header.className = 'webshop-banner';
        header.innerHTML = `<div class="webshop-banner-icon">\uD83C\uDFE0</div><div style="display:flex;align-items:flex-end;gap:8px"><div class="webshop-banner-title">${L.get('shop.apt.title') || 'HomeStyle'}</div><div class="webshop-banner-sub">${L.get('shop.apt.subtitle') || 'Upgrade your apartment'}</div></div>`;
        container.appendChild(header);

        // Amazon-style search bar (decorative)
        const search = document.createElement('div');
        search.className = 'webshop-search';
        search.style.background = 'rgb(19, 25, 33)';
        search.innerHTML = `
            <div class="webshop-search" style="flex:1;margin:0 16px 10px;">
                <span class="webshop-search-text">${L.get('shop.apt.search') || 'Search upgrades...'}</span>
                <div class="webshop-search-btn">\uD83D\uDD0D</div>
            </div>
        `;
        container.appendChild(search);

        const grid = document.createElement('div');
        grid.className = 'webshop-grid';

        for (const upgrade of APARTMENT_UPGRADES) {
            const currentTier = story ? (story.variablesState[upgrade.id] || 0) : 0;
            const nextTier = upgrade.tiers.find(t => t.tier === currentTier + 1);
            const displayTier = nextTier || upgrade.tiers[upgrade.tiers.length - 1];

            const upgradeName = (upgrade.nameKey ? L.get(upgrade.nameKey) : null) || upgrade.fallbackName;
            const nextTierName = nextTier ? ((nextTier.nameKey ? L.get(nextTier.nameKey) : null) || nextTier.fallbackName) : '';
            const nextTierDesc = nextTier ? ((nextTier.descKey ? L.get(nextTier.descKey) : null) || nextTier.fallbackDesc) : '';
            const fullyUpgraded = L.get('shop.fully_upgraded') || 'Fully upgraded';

            const card = document.createElement('div');
            card.className = 'webshop-product' + (!nextTier ? ' webshop-maxed' : '');
            const _ri2 = window._pno_resolveImage;
            const imgSrc = (_ri2 ? _ri2('Apartment', `${upgrade.id}_${displayTier.tier}`) : null) || `assets/images/Apartment/${upgrade.id}_${displayTier.tier}.png`;
            card.innerHTML = `
                <div class="webshop-product-img"><img src="${imgSrc}" alt="${upgradeName}" onerror="this.style.display='none';if(this.parentElement)this.parentElement.textContent='${displayTier.icon}'"></div>
                <div class="webshop-product-info">
                    <div class="webshop-product-name">${upgradeName}</div>
                    <div class="webshop-product-tier">${'\u2605'.repeat(currentTier)}${'\u2606'.repeat(Math.max(0, upgrade.tiers.length - currentTier))}</div>
                    <div class="webshop-product-desc">${nextTier ? `${nextTierName} \u2014 ${nextTierDesc}` : fullyUpgraded}</div>
                    ${nextTier ? `<div class="webshop-product-price">$${nextTier.price}</div>` : ''}
                </div>
            `;

            if (nextTier) {
                const canAfford = this._money >= nextTier.price;
                const btn = document.createElement('button');
                btn.className = 'webshop-buy-btn' + (canAfford ? '' : ' webshop-buy-disabled');
                btn.textContent = canAfford ? (L.get('shop.buy_now') || 'Buy Now') : (L.get('shop.not_enough') || 'Not enough');
                btn.disabled = !canAfford;
                const capturedUpgrade = upgrade;
                const capturedTier = nextTier;
                btn.addEventListener('click', () => {
                    const s = this._storyReader?.();
                    if (!s) { console.error('[Shop] No story ref'); return; }
                    const money = variableManager.getTotalValue('Money') || 0;
                    if (money < capturedTier.price) return;
                    events.emit('shop:deductMoney', capturedTier.price);
                    this._money = variableManager.getTotalValue('Money') || 0;
                    try {
                        s.variablesState[capturedUpgrade.id] = capturedTier.tier;
                        if (capturedUpgrade.id === 'upgrade_bed') {
                            variableManager.evalSafe('update_max_energy');
                        }
                        // Decor T3 (Sanctuary) adds +5 max Energy via the
                        // get_max_energy() check on upgrade_decor >= 3.
                        // Recalc immediately so the player sees the boost
                        // without waiting for the next overnight.
                        if (capturedUpgrade.id === 'upgrade_decor' && capturedTier.tier >= 3) {
                            variableManager.evalSafe('update_max_energy');
                        }
                        // upgrade_vanity T1+ contributes to apartment_nest_tier
                        // (substitutes for the dropped decor_vanity_table) — recompute
                        // immediately so the next render reflects the new tier.
                        if (capturedUpgrade.id === 'upgrade_vanity' && capturedTier.tier === 1) {
                            try { variableManager.evalSafe('recompute_nest_tier', []); }
                            catch(e) { console.warn('[Shop] recompute_nest_tier failed:', e?.message); }
                            try { appearanceState.invalidateCache(); } catch(e) {}
                        }
                        if (capturedTier.tier >= 3) {
                            events.emit(Events.TAG_ACHIEVEMENT, capturedUpgrade.id + '_max');
                        }
                    } catch(e) { console.error('[Shop] Ink var set failed:', e); }
                    events.emit(Events.AUDIO_SFX_PLAY, 'item_purchase1');
                    card.classList.add('webshop-just-bought');
                    setTimeout(() => { this._renderItems(); this._updateMoneyDisplay(); }, 500);
                });
                card.querySelector('.webshop-product-info').appendChild(btn);
            }

            grid.appendChild(card);
        }
        container.appendChild(grid);

        // === Decor section (boolean items, contribute to apartment_nest_tier) ===
        this._renderBooleanShopSection(container, APARTMENT_DECOR, {
            sectionTitle: L.get('shop.apt.decor_header', 'Decor & Style'),
            sectionSub: L.get('shop.apt.decor_sub', 'Each piece reshapes the apartment.'),
        });

        // === Expansions section (basement, playroom, equipment rack) ===
        this._renderBooleanShopSection(container, APARTMENT_EXPANSIONS, {
            sectionTitle: L.get('shop.apt.expand_header', 'Expansions'),
            sectionSub: L.get('shop.apt.expand_sub', 'Unlock more of the apartment.'),
        });
    }

    // Reusable webshop renderer for boolean-VAR shops (decor, expansions).
    // Each item: { inkVar, nameKey, fallbackName, price, descKey, fallbackDesc,
    //   icon, img, req?: {VAR: minValue}, patreonOnly?: bool }
    _renderBooleanShopSection(container, list, opts) {
        const story = this._storyReader?.();

        const sectionHeader = document.createElement('div');
        sectionHeader.className = 'webshop-section-header';
        sectionHeader.innerHTML = `<div class="webshop-section-title">${opts.sectionTitle}</div><div class="webshop-section-sub">${opts.sectionSub || ''}</div>`;
        container.appendChild(sectionHeader);

        const grid = document.createElement('div');
        grid.className = 'webshop-grid';

        const isPatron = isPat();
        const _ri = window._pno_resolveImage;

        // Friendly labels for req-VAR names → "Locked (apartment_nest_tier 2+)"
        // reads as raw Ink VAR otherwise. Boolean reqs get a sentence form.
        const REQ_LABELS = {
            apartment_nest_tier: L.get('shop.req.nest_tier', 'Nest Tier'),
            apartment_has_basement: L.get('shop.req.basement', 'Basement leased'),
            apartment_has_playroom: L.get('shop.req.playroom', 'Playroom converted'),
            Femininity: L.get('stat.fem', 'Femininity'),
            Submissiveness: L.get('stat.sub', 'Submissiveness'),
            Masculinity: L.get('stat.masc', 'Masculinity'),
            Day: L.get('stat.day', 'Day'),
            MasterRelationship: L.get('stat.master_rel', 'Master Relationship'),
        };

        for (const item of list) {
            const owned = story ? !!story.variablesState[item.inkVar] : false;
            // Visibility gate: check req fields against current story VARs
            let reqMet = true;
            let reqMsg = '';
            if (item.req && story) {
                for (const [k, min] of Object.entries(item.req)) {
                    const cur = story.variablesState[k] || 0;
                    const need = (typeof min === 'boolean') ? (min ? 1 : 0) : Number(min);
                    const curNum = (typeof cur === 'boolean') ? (cur ? 1 : 0) : Number(cur);
                    if (curNum < need) {
                        reqMet = false;
                        const label = REQ_LABELS[k] || k;
                        // Boolean reqs render as a sentence ("Basement leased"); numeric reqs show "Label N+"
                        reqMsg = (typeof min === 'boolean') ? label : `${label} ${need}+`;
                        break;
                    }
                }
            }
            if (item.patreonOnly && !isPatron && !owned) {
                reqMet = false;
                reqMsg = 'Patreon';
            }
            const canAfford = this._money >= item.price;

            // L.get returns the key string when missing — falls through to fallback via 2nd-arg API
            const itemName = item.nameKey ? L.get(item.nameKey, item.fallbackName) : item.fallbackName;
            const itemDesc = item.descKey ? L.get(item.descKey, item.fallbackDesc) : item.fallbackDesc;
            // Defensive: L.get may still return the key on some edge cases — re-check
            const safeName = (itemName === item.nameKey) ? item.fallbackName : itemName;
            const safeDesc = (itemDesc === item.descKey) ? item.fallbackDesc : itemDesc;
            const ownedBadge = L.get('shop.owned') || 'Owned';
            const lockedLabel = (L.get('shop.locked') || 'Locked') + (reqMsg ? ` (${reqMsg})` : '');

            const card = document.createElement('div');
            card.className = 'webshop-product' + (owned ? ' webshop-maxed' : (reqMet ? '' : ' webshop-locked'));
            const imgSrc = item.img ? ((_ri ? _ri('Items', item.img) : null) || `assets/images/Items/${item.img}.png`) : null;
            const imgEl = imgSrc ? `<img src="${imgSrc}" alt="${safeName}" onerror="this.style.display='none';if(this.parentElement)this.parentElement.textContent='${item.icon}'">` : item.icon;
            card.innerHTML = `
                <div class="webshop-product-img">${imgEl}</div>
                <div class="webshop-product-info">
                    <div class="webshop-product-name">${safeName}</div>
                    <div class="webshop-product-desc">${safeDesc}</div>
                    ${owned ? `<div class="webshop-owned-badge">✓ ${ownedBadge}</div>` : `<div class="webshop-product-price">$${item.price}</div>`}
                </div>
            `;

            if (!owned) {
                const btn = document.createElement('button');
                if (!reqMet) {
                    btn.className = 'webshop-buy-btn webshop-buy-disabled';
                    btn.textContent = lockedLabel;
                    btn.disabled = true;
                } else {
                    btn.className = 'webshop-buy-btn' + (canAfford ? '' : ' webshop-buy-disabled');
                    btn.textContent = canAfford ? (L.get('shop.add_to_cart') || 'Add to Cart') : (L.get('shop.not_enough') || 'Not enough');
                    btn.disabled = !canAfford;
                }
                const capturedItem = item;
                btn.addEventListener('click', () => {
                    if (!reqMet || !canAfford) return;
                    const s = this._storyReader?.();
                    if (!s) { console.error('[Shop] No story ref'); return; }
                    const money = variableManager.getTotalValue('Money') || 0;
                    if (money < capturedItem.price) return;
                    events.emit('shop:deductMoney', capturedItem.price);
                    this._money = variableManager.getTotalValue('Money') || 0;
                    try {
                        // Non-observed Ink VARs (decor_*/apartment_has_*/perk_*) must be
                        // written to story.variablesState ONLY. A variableManager.setFlag
                        // here would create a baseVar shadow that drifts on save/load
                        // (see .claude/rules/save-state-drift.md rule #1).
                        s.variablesState[capturedItem.inkVar] = true;
                        // Decor purchases drive apartment_nest_tier — recompute now
                        // + invalidate appearance cache so apartment_* image variants
                        // pick up the new tier suffix on next refresh.
                        if (capturedItem.inkVar && capturedItem.inkVar.startsWith('decor_')) {
                            try { variableManager.evalSafe?.('recompute_nest_tier', []); }
                            catch (e) { console.warn('[Shop] recompute_nest_tier failed:', e?.message); }
                            try { appearanceState.invalidateCache(); } catch(e) {}
                        }
                    } catch(e) { console.error('[Shop] Ink var set failed:', e); }
                    events.emit(Events.AUDIO_SFX_PLAY, 'item_purchase1');
                    card.classList.add('webshop-just-bought');
                    setTimeout(() => { this._renderItems(); this._updateMoneyDisplay(); }, 500);
                });
                card.querySelector('.webshop-product-info').appendChild(btn);
            }

            grid.appendChild(card);
        }
        container.appendChild(grid);
    }

    // Webshop-style stream equipment
    _renderStreamEquipment(container) {
        const header = document.createElement('div');
        header.className = 'webshop-banner';
        header.style.setProperty('--shop-accent', '#e06878');
        header.innerHTML = `<div class="webshop-banner-icon">\uD83D\uDCF9</div><div><div class="webshop-banner-title">${L.get('shop.stream.title') || 'StreamHub Shop'}</div><div class="webshop-banner-sub">${L.get('shop.stream.subtitle') || 'Level up your setup'}</div></div>`;
        container.appendChild(header);

        const grid = document.createElement('div');
        grid.className = 'webshop-grid';

        for (const equip of STREAM_EQUIPMENT) {
            const story = this._storyReader?.();
            const owned = story ? !!story.variablesState[equip.inkVar] : false;
            const canAfford = this._money >= equip.price;

            const equipName = (equip.nameKey ? L.get(equip.nameKey) : null) || equip.fallbackName;
            const equipDesc = (equip.descKey ? L.get(equip.descKey) : null) || equip.fallbackDesc;
            const ownedBadge = L.get('shop.owned') || 'Owned';

            const card = document.createElement('div');
            card.className = 'webshop-product' + (owned ? ' webshop-maxed' : '');
            const _ri3 = window._pno_resolveImage;
            const equipSrc = equip.img ? ((_ri3 ? _ri3('Items', equip.img) : null) || `assets/images/Items/${equip.img}.png`) : null;
            const equipImg = equipSrc ? `<img src="${equipSrc}" alt="${equipName}" onerror="this.style.display='none';if(this.parentElement)this.parentElement.textContent='${equip.icon}'">` : equip.icon;
            card.innerHTML = `
                <div class="webshop-product-img">${equipImg}</div>
                <div class="webshop-product-info">
                    <div class="webshop-product-name">${equipName}</div>
                    <div class="webshop-product-desc">${equipDesc}</div>
                    ${owned ? `<div class="webshop-owned-badge">\u2713 ${ownedBadge}</div>` : `<div class="webshop-product-price">$${equip.price}</div>`}
                </div>
            `;

            if (!owned) {
                const btn = document.createElement('button');
                btn.className = 'webshop-buy-btn' + (canAfford ? '' : ' webshop-buy-disabled');
                btn.textContent = canAfford ? (L.get('shop.add_to_cart') || 'Add to Cart') : (L.get('shop.not_enough') || 'Not enough');
                btn.disabled = !canAfford;
                const capturedEquip = equip;
                btn.addEventListener('click', () => {
                    const s = this._storyReader?.();
                    if (!s) { console.error('[Shop] No story ref'); return; }
                    const money = variableManager.getTotalValue('Money') || 0;
                    if (money < capturedEquip.price) return;
                    events.emit('shop:deductMoney', capturedEquip.price);
                    this._money = variableManager.getTotalValue('Money') || 0;
                    try {
                        s.variablesState[capturedEquip.inkVar] = 1;
                        s.variablesState['webcam_equipment'] = 1;
                    } catch(e) { console.error('[Shop] Ink var set failed:', e); }
                    events.emit(Events.AUDIO_SFX_PLAY, 'item_purchase1');
                    card.classList.add('webshop-just-bought');
                    setTimeout(() => { this._renderItems(); this._updateMoneyDisplay(); }, 500);
                });
                card.querySelector('.webshop-product-info').appendChild(btn);
            }

            grid.appendChild(card);
        }
        container.appendChild(grid);
    }

    // Inkwell tattoo studio shop
    _renderTattooShop(container) {
        const story = this._storyReader?.();
        const fem = story ? (story.variablesState['Femininity'] || 0) : 0;
        const energy = story ? (story.variablesState['Energy'] || 0) : 0;
        const isPatBuild = isPat();

        // Category tabs (banner already rendered by _buildShopUI from SHOP_TYPES)
        const categories = [
            { id: 'all', label: 'All Designs' },
            { id: 'feminine', label: 'Feminine', color: '#e88cb8' },
            { id: 'masculine', label: 'Masculine', color: '#5888cc' },
            { id: 'neutral', label: 'Neutral', color: '#aaa' },
        ];
        const filterBar = document.createElement('div');
        filterBar.className = 'shop-filters';
        let activeFilter = 'all';
        for (const cat of categories) {
            const tab = document.createElement('span');
            tab.className = 'shop-filter-tab' + (cat.id === 'all' ? ' active' : '');
            tab.textContent = cat.label;
            if (cat.color) tab.style.borderColor = cat.color;
            tab.addEventListener('click', () => {
                activeFilter = cat.id;
                filterBar.querySelectorAll('.shop-filter-tab').forEach(t => t.classList.remove('active'));
                tab.classList.add('active');
                renderCards();
            });
            filterBar.appendChild(tab);
        }
        container.appendChild(filterBar);

        const grid = document.createElement('div');
        grid.className = 'webshop-grid';
        container.appendChild(grid);

        const _ri = window._pno_resolveImage;

        const renderCards = () => {
            grid.innerHTML = '';
            const designs = activeFilter === 'all' ? TATTOO_DESIGNS : TATTOO_DESIGNS.filter(d => d.category === activeFilter);

            for (const tattoo of designs) {
                const owned = story ? !!story.variablesState[tattoo.inkVar] : false;
                const canAfford = this._money >= tattoo.price;
                const hasEnergy = energy >= tattoo.energy;
                const femOk = (!tattoo.femReq || fem >= tattoo.femReq) && (!tattoo.femMax || fem < tattoo.femMax);
                const patOk = !tattoo.patreon || isPatBuild;
                const canBuy = !owned && canAfford && hasEnergy && femOk && patOk;

                const catColor = tattoo.category === 'feminine' ? '#e88cb8' : tattoo.category === 'masculine' ? '#5888cc' : '#aaa';
                const imgSrc = (_ri ? _ri('Tattoo', tattoo.img) : null) || `assets/images/Tattoo/${tattoo.img}.png`;

                const card = document.createElement('div');
                card.className = 'webshop-product' + (owned ? ' webshop-maxed' : '');
                const tName = _tatName(tattoo);
                const tDesc = _tatDesc(tattoo);
                const tLocation = _tatLocation(tattoo);
                card.innerHTML = `
                    <div class="webshop-product-img"><img src="${imgSrc}" alt="${tName}" onerror="this.style.display='none';if(this.parentElement)this.parentElement.textContent='\uD83D\uDD89'"></div>
                    <div class="webshop-product-info">
                        <div class="webshop-product-name">${tName}</div>
                        <div style="font-size:0.7em;color:${catColor};margin:-2px 0 4px;">${tLocation} \u2022 ${L.get('shop.tattoo.cat.' + tattoo.category, tattoo.category)}</div>
                        <div class="webshop-product-desc">${tDesc}</div>
                        ${owned ? `<div class="webshop-owned-badge">\u2713 ${L.get('shop.tattoo.owned_badge', 'Inked')}</div>` :
                          `<div class="webshop-product-price">$${tattoo.price}</div>
                           <div style="font-size:0.65em;color:var(--text-dim,#888);margin-top:2px;">${L.get('shop.tattoo.energy_label', 'Energy')}: ${tattoo.energy}${tattoo.femReq ? ' \u2022 ' + L.get('shop.tattoo.fem_req', 'Fem') + ' '+tattoo.femReq+'+' : ''}${tattoo.femMax ? ' \u2022 ' + L.get('shop.tattoo.fem_max', 'Fem') + ' <'+tattoo.femMax : ''}${tattoo.patreon ? ' \u2022 Patreon' : ''}</div>`}
                    </div>
                `;

                if (!owned) {
                    const btn = document.createElement('button');
                    btn.className = 'webshop-buy-btn' + (canBuy ? '' : ' webshop-buy-disabled');
                    if (!canBuy) {
                        btn.textContent = !patOk
                            ? L.get('shop.tattoo.btn_patreon_only', 'Patreon Only')
                            : !femOk
                                ? (tattoo.femReq
                                    ? L.get('shop.tattoo.btn_need_fem', 'Need Fem {0}+').replace('{0}', String(tattoo.femReq))
                                    : L.get('shop.tattoo.btn_fem_too_high', 'Fem too high'))
                                : !canAfford
                                    ? L.get('shop.tattoo.btn_not_enough_money', 'Not enough $')
                                    : !hasEnergy
                                        ? L.get('shop.tattoo.btn_not_enough_energy', 'Not enough energy')
                                        : L.get('shop.tattoo.btn_unavailable', 'Unavailable');
                    } else {
                        btn.textContent = L.get('shop.tattoo.btn_get_inked', 'Get Inked');
                    }
                    btn.disabled = !canBuy;
                    const capturedTattoo = tattoo;
                    btn.addEventListener('click', async () => {
                        const s = this._storyReader?.();
                        if (!s) return;
                        const money = variableManager.getTotalValue('Money') || 0;
                        if (money < capturedTattoo.price) return;
                        // Audit D02: tattoos are permanent and high-cost. Show a
                        // confirm dialog before applying. No cooldown needed
                        // since the confirm itself is the friction.
                        const proceed = await _confirmPermanentPurchase({
                            title: L.get('shop.tattoo.confirm_title', 'Permanent Tattoo'),
                            itemName: _tatName(capturedTattoo),
                            location: _tatLocation(capturedTattoo),
                            price: capturedTattoo.price,
                            warning: L.get('shop.tattoo.confirm_warning', 'Tattoos are permanent. They cannot be removed later in this save.'),
                            confirmLabel: L.get('shop.tattoo.confirm_label', 'Get inked'),
                        });
                        if (!proceed) return;
                        // Re-check money after the await — player could have
                        // spent it elsewhere during the dialog.
                        const moneyNow = variableManager.getTotalValue('Money') || 0;
                        if (moneyNow < capturedTattoo.price) return;
                        // Deduct money
                        events.emit('shop:deductMoney', capturedTattoo.price);
                        this._money = variableManager.getTotalValue('Money') || 0;
                        // Set tattoo flag
                        try { s.variablesState[capturedTattoo.inkVar] = true; } catch(e) { console.warn('[Shop] tattoo flag set failed:', e?.message); }
                        // Apply stat changes
                        for (const sc of capturedTattoo.statChanges) {
                            if (sc.fn) {
                                // Call Ink function (fem_gain, sub_gain, shame_gain)
                                variableManager.evalSafe(sc.fn, [sc.val]);
                            } else if (sc.stat) {
                                // Direct stat change
                                try {
                                    const cur = s.variablesState[sc.stat] || 0;
                                    const nv = Math.max(0, cur + sc.val);
                                    s.variablesState[sc.stat] = nv;
                                } catch(e) { console.warn('[Shop] tattoo stat change failed:', e?.message); }
                            }
                        }
                        // Deduct energy
                        try {
                            const curE = s.variablesState['Energy'] || 0;
                            s.variablesState['Energy'] = Math.max(0, curE - capturedTattoo.energy);
                        } catch(e) { console.warn('[Shop] tattoo energy deduct failed:', e?.message); }
                        events.emit(Events.AUDIO_SFX_PLAY, 'item_purchase1');
                        card.classList.add('webshop-just-bought');
                        setTimeout(() => { this._money = variableManager.getTotalValue('Money') || 0; renderCards(); this._updateMoneyDisplay(); }, 500);
                    });
                    card.querySelector('.webshop-product-info').appendChild(btn);
                }

                grid.appendChild(card);
            }
        };
        renderCards();
    }

    _createContainer() {
        const el = document.createElement('div');
        el.id = 'overlay-shop';
        el.className = 'overlay-container hidden';
        document.body.appendChild(el);
        return el;
    }
}

/** @type {ShopUI} */
export const shopUI = new ShopUI();
